import React from "react";
import { TrendingUp, TrendingDown, DollarSign, Package, Clock, Award, Sparkles } from "lucide-react";

export function ProviderAnalytics({ analytics, formatPrice, accent = "#FF6B00", lang = "en" }) {
  if (!analytics) return null;
  const { totals, top_items, revenue_chart } = analytics;
  const isAr = lang === "ar";

  // Chart scaling
  const maxRev = Math.max(...revenue_chart.map(d => d.revenue), 1);

  const label = (en, ar) => (isAr ? ar : en);
  const growthUp = totals.week_growth_pct >= 0;

  return (
    <div className="so-card p-5" data-testid="provider-analytics">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5" style={{ color: accent }} />
        <div className="font-bold text-lg">{label("Business analytics", "تحليلات الأعمال")}</div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--so-border)] text-[var(--so-text-soft)]">{label("Last 7 days", "آخر 7 أيام")}</span>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-kpis">
        <Kpi
          testId="kpi-revenue-week"
          icon={DollarSign}
          label={label("Revenue this week", "إيرادات هذا الأسبوع")}
          value={formatPrice(totals.revenue_week)}
          trend={totals.week_growth_pct === 0 ? null : { up: growthUp, pct: Math.abs(totals.week_growth_pct) }}
          accent={accent}
        />
        <Kpi
          testId="kpi-completed"
          icon={Package}
          label={label("Completed", "مكتمل")}
          value={String(totals.bookings_completed)}
          hint={`${totals.completion_rate}% ${label("completion", "معدل الإتمام")}`}
          accent={accent}
        />
        <Kpi
          testId="kpi-active"
          icon={Clock}
          label={label("Active bookings", "الحجوزات النشطة")}
          value={String(totals.bookings_active)}
          hint={`${totals.bookings_pending} ${label("pending", "بانتظار")}`}
          accent={accent}
        />
        <Kpi
          testId="kpi-response"
          icon={Award}
          label={label("Avg response", "متوسط الاستجابة")}
          value={totals.avg_response_min ? `${totals.avg_response_min}m` : "—"}
          hint={label("to confirm", "للتأكيد")}
          accent={accent}
        />
      </div>

      {/* Revenue chart */}
      <div className="mt-6" data-testid="analytics-chart">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">{label("Revenue trend (7d)", "اتجاه الإيرادات (7 أيام)")}</div>
          <div className="text-sm">
            <span className="text-[var(--so-text-soft)]">{label("Total: ", "إجمالي: ")}</span>
            <span className="font-bold" style={{ color: accent }}>{formatPrice(totals.revenue_total)}</span>
          </div>
        </div>
        <div className="flex items-end gap-2 h-32 px-1">
          {revenue_chart.map((d, idx) => {
            // Empty days sit at 2px, otherwise scale proportionally so the peak day is fully 100%
            const h = maxRev > 0 && d.revenue > 0 ? (d.revenue / maxRev) * 100 : 0;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center gap-1 group" data-testid={`chart-day-${idx}`}>
                <div className="text-[10px] font-semibold text-[var(--so-text-soft)] opacity-0 group-hover:opacity-100 transition-opacity">
                  {formatPrice(d.revenue)}
                </div>
                <div className="w-full rounded-t-md transition-all hover:brightness-110" style={{ height: d.revenue > 0 ? `${h}%` : "3px", background: d.revenue > 0 ? `linear-gradient(180deg, ${accent} 0%, ${accent}88 100%)` : "var(--so-border)", minHeight: "3px" }} />
                <div className="text-[10px] text-[var(--so-text-soft)] font-semibold">{d.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top items */}
      {top_items.length > 0 && (
        <div className="mt-6" data-testid="analytics-top-items">
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)] mb-2">{label("Top-selling items", "الأعلى مبيعًا")}</div>
          <div className="space-y-2">
            {top_items.map((it, idx) => (
              <div key={it.item_id} className="flex items-center gap-3" data-testid={`top-item-${idx}`}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: `${accent}1A`, color: accent }}>{idx + 1}</div>
                {it.image_url && <img src={it.image_url} alt={it.name} className="w-10 h-10 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{it.name}</div>
                  <div className="text-xs text-[var(--so-text-soft)]">{it.quantity} {label("sold", "مباع")}</div>
                </div>
                <div className="text-sm font-bold" style={{ color: accent }}>{formatPrice(it.revenue)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {top_items.length === 0 && totals.bookings_total === 0 && (
        <div className="mt-6 text-center text-sm text-[var(--so-text-soft)] py-4">
          {label("No data yet — your analytics will grow with each booking.", "لا توجد بيانات بعد — ستنمو التحليلات مع كل حجز.")}
        </div>
      )}
    </div>
  );
}

function Kpi({ testId, icon: Icon, label, value, hint, trend, accent }) {
  return (
    <div className="p-3 rounded-xl border border-[var(--so-border)]" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${accent}15`, color: accent }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--so-text-soft)] leading-tight flex-1">{label}</div>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-xl font-extrabold" style={{ color: accent }}>{value}</div>
        {trend && (
          <div className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${trend.up ? "text-emerald-600" : "text-red-500"}`}>
            {trend.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {trend.pct}%
          </div>
        )}
      </div>
      {hint && <div className="text-[10px] text-[var(--so-text-soft)] mt-0.5">{hint}</div>}
    </div>
  );
}
